import styled, { css } from 'styled-components'
import { useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import debounce from 'lodash/debounce'

import { api } from '@h2/api'
import { PB97T, SDB10T, SDB10S } from '@h2/components/colors'
import { Flex } from '@h2/components/layout/flex'
import { Box } from '@h2/components/layout/box'
import { Button } from '@h2/components/buttons/button'
import { features } from '@h2/components/media'
import { lists } from '@h2/components/mixins'
import { Globals } from '@h2/globals'

import { Input } from './input'
import { Icon } from './icon'

import { useNavigator } from '../../hooks/useNavigator'
import { useCallbackOnEventOutside } from '../../hooks/useCallbackOnEventOutside'
import { ReturnToNaviance } from '../returnToNaviance'

const FlexWrap = styled(Flex)`
  display: none;
  position: relative;

  ${({ isNavianceUser }) =>
    isNavianceUser &&
    css`
      @media (${features.minWidth.laptop}) {
        display: flex;
      }
    `}

  @media (${features.minWidth.desktop}) {
    display: flex;

    form {
      min-width: 275px;

      > label {
        display: inline-block;
        width: 100%;

        > span:first-child {
          display: none;
        }
      }
    }
  }
`
const ResultsBox = styled(Box)`
  position: absolute;
  right: 0;
  top: 40px;
  z-index: 1000;
`
const List = styled.ul`
  ${lists};
  background-color: white;
  padding: 15px;
  border: 5px solid ${PB97T};
  border-right: none;
  border-left: none;
  box-shadow: ${PB97T} 2px 2px 2px;

  > li {
    display: flex;
    align-items: center;
    margin: 0 0 5px 0;
    padding: 3px;

    &:last-child {
      margin: 0;
    }

    &:hover {
      background-color: ${PB97T};
    }

    button {
      color: ${SDB10T};
      font-size: 20px;
      font-weight: 400;
      padding: 5px 8px;
      white-space: pre-wrap;

      &:hover {
        color: ${SDB10S};
        text-decoration: underline;
      }
    }
  }
`
const isValidQuery = string => /\w{3}/.test(string ?? '')
const Search = ({ renderSearch }) => {
  const goTo = useNavigator()
  const input = useRef(null)
  const results = useRef(null)
  const location = useLocation()
  const [suggestions, setSuggestions] = useState([])
  const handleOnInput = useMemo(() => {
    return debounce(
      async ({ target }) => {
        if (isValidQuery(target.value)) {
          const encoded = encodeURIComponent(target.value.trim())
          const suggs = await api.fetch(`/search/suggest/${encoded}`, {
            version: 'v1'
          })

          setSuggestions(suggs)
        } else {
          setSuggestions([])
        }
      },
      250,
      { leading: true }
    )
  }, [setSuggestions])
  const handleOnSubmit = useCallback(
    evt => {
      evt.preventDefault()
      goTo(`/search?q=${input.current.value}`)
    },
    [goTo]
  )
  const reset = useCallback(() => {
    if (input.current) {
      input.current.value = ''
      input.current.blur()
    }

    setSuggestions([])
  }, [setSuggestions])

  useEffect(() => {
    reset()
  }, [location, reset])

  useCallbackOnEventOutside(
    useMemo(() => [results, input], [results, input]),
    reset
  )

  const { isNavianceUser } = useContext(Globals)

  return (
    <FlexWrap isNavianceUser={isNavianceUser}>
      {!renderSearch ? (
        <Box>
          <form onSubmit={handleOnSubmit}>
            <label aria-label="Search">
              <span>Search</span>
              <Input ref={input} onInput={handleOnInput} />
            </label>
          </form>
        </Box>
      ) : (
        <ReturnToNaviance />
      )}
      {suggestions.length > 0 && (
        <ResultsBox width="480px" ref={results}>
          <List>
            {suggestions.map(({ label, value, type }) => (
              <li key={label}>
                <Icon type={type} />
                <Button
                  isFullWidth
                  variant="link"
                  onClick={() => {
                    goTo(value)
                  }}>
                  {label}
                </Button>
              </li>
            ))}
          </List>
        </ResultsBox>
      )}
    </FlexWrap>
  )
}

export { Search }
